import { useEffect, useState, useRef } from "react";
import { ShieldCheck, Upload, CheckCircle2, XCircle, Clock, AlertTriangle, FileText, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_LABEL = {
  application_started: "Application Started", documents_required: "Documents Required",
  documents_submitted: "Documents Submitted", ai_review: "AI Review", admin_review: "Under Admin Review",
  additional_info_required: "Additional Info Required", approved: "Approved", rejected: "Rejected",
  suspended: "Suspended", expired: "Expired", reverification_required: "Re-verification Required",
};
const DOC_LABEL = {
  required: "Required", not_uploaded: "Not Uploaded", uploaded: "Uploaded", ai_checking: "AI Checking",
  admin_review: "Admin Review", approved: "Approved", rejected: "Rejected", expired: "Expired",
  replacement_required: "Replacement Required",
};
const badge = (s) => s === "approved" ? "bg-emerald-100 text-emerald-700"
  : ["rejected", "suspended", "expired", "replacement_required"].includes(s) ? "bg-rose-100 text-rose-700"
  : ["admin_review", "documents_submitted", "ai_review", "ai_checking", "additional_info_required", "reverification_required"].includes(s) ? "bg-amber-100 text-amber-700"
  : "bg-slate-100 text-slate-600";

const readB64 = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

import { AIDocAssist } from "@/components/AIDocAssist";

// Map a seller requirement code → the AI doc-assist schema it should read.
const DOC_TYPE_BY_CODE = { gstin: "gst", gst: "gst", pan_card: "pan", pan: "pan", fssai: "fssai",
  cancelled_cheque: "bank_proof", bank_proof: "bank_proof", bank: "bank_proof", trade_license: "trade_license",
  trade: "trade_license", shop_act: "trade_license", license: "trade_license", licence: "trade_license",
  aadhaar: "aadhaar", aadhar: "aadhaar", driving: "driving_license", dl: "driving_license",
  insurance: "vehicle_insurance", permit: "vehicle_permit", rc: "vehicle_rc" };
const docTypeForCode = (code) => { const c = (code || "").toLowerCase(); for (const k in DOC_TYPE_BY_CODE) if (c.includes(k)) return DOC_TYPE_BY_CODE[k]; return "auto"; };

export default function SellerVerification() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ seller_name: "", business_type: "", contact_person: "", phone: "", address: "", state: "", district: "", pincode: "", pan: "", gst: "", intended_products: "" });
  const [saving, setSaving] = useState(false);
  const [cats, setCats] = useState([]);
  const [catIds, setCatIds] = useState([]);
  const fileRefs = useRef({});

  const load = () => api.get("/seller/application").then((r) => {
    setData(r.data);
    if (r.data.application) {
      setForm((f) => ({ ...f, ...Object.fromEntries(Object.keys(f).map((k) => [k, r.data.application[k] ?? ""])) }));
      setCatIds((r.data.application.seller_categories || []).map((c) => c.category_id));
    }
  }).catch(() => {});
  useEffect(() => { load(); api.get("/marketplace/categories").then((r) => setCats((r.data.categories || []).filter((c) => c.active !== false))).catch(() => {}); }, []);

  const app = data?.application;
  const docs = data?.documents || [];
  const reqs = app?.requirements || [];
  const catName = (id) => cats.find((c) => c.category_id === id)?.name || id;
  const catStatus = (id) => (app?.seller_categories || []).find((c) => c.category_id === id) || { status: "pending" };
  const addCat = (id) => { if (id && !catIds.includes(id)) setCatIds([...catIds, id]); };
  const removeCat = (id) => setCatIds(catIds.filter((x) => x !== id));
  const saveCats = async () => {
    try { await api.post("/seller/categories", { category_ids: catIds }); toast.success("Categories updated — pending admin review"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveInfo = async () => {
    if (!form.seller_name.trim()) { toast.error("Enter your business name"); return; }
    setSaving(true);
    try {
      await api.post("/seller/application", { ...form, country: "IN", intended_categories: [], category_ids: catIds });
      toast.success("Saved — required documents identified below");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const upload = async (code, name, file, expiry) => {
    if (!file) return;
    try {
      const b64 = await readB64(file);
      const r = await api.post("/seller/documents/upload", { code, name, filename: file.name, content_b64: b64, expiry_date: expiry || null });
      toast.success(`${name} uploaded`);
      if (r.data.ai_findings?.length) toast.message("AI findings", { description: r.data.ai_findings.join("; ") });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Optional AI auto-fill → map extracted document fields onto the business form. Never auto-saves.
  const applyDocFields = (f) => {
    setForm((prev) => ({ ...prev,
      gst: f.gstin || prev.gst,
      pan: f.pan || prev.pan,
      seller_name: prev.seller_name || f.legal_name || f.trade_name || f.holder || f.account_holder || f.name || prev.seller_name,
      address: prev.address || f.address || prev.address,
    }));
  };

  const submit = async () => {
    try { await api.post("/seller/application/submit"); toast.success("Application submitted for review"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const docFor = (code) => docs.find((d) => d.code === code);
  const s = data?.summary;

  return (
    <div data-testid="seller-verification-page">
      <PageHeader title="Verification & Compliance" icon={ShieldCheck}
        subtitle="Get verified by Suffle to start selling. Requirements depend on what you plan to sell." />

      {app && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="seller-status-summary">
          {[["Business Info", s?.business_complete ? "Completed" : "Pending", s?.business_complete],
            ["Documents", `${s?.submitted || 0} / ${s?.required_total || 0} uploaded`, (s?.submitted || 0) > 0],
            ["AI Review", app.ai_note ? "Assisted" : "—", true],
            ["Overall Status", STATUS_LABEL[app.status] || app.status, app.status === "approved"]].map(([t, v, ok]) => (
            <div key={t} className="card-soft p-3">
              <p className="text-[11px] uppercase tracking-wide text-slate-400">{t}</p>
              <p className={`mt-1 text-sm font-semibold ${ok ? "text-emerald-600" : "text-slate-700"}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {app?.status === "approved" && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700" data-testid="seller-approved-banner">
          <CheckCircle2 className="h-5 w-5" /> You're a verified Suffle seller. Product onboarding opens in a later step.
        </div>
      )}
      {app?.status === "rejected" && app?.decision_reason && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"><XCircle className="mt-0.5 h-4 w-4" /> Application not approved. Reason: {app.decision_reason}</div>
      )}
      {app?.status === "additional_info_required" && app?.info_request && (
        <div className="mb-5 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700" data-testid="seller-info-request"><AlertTriangle className="mt-0.5 h-4 w-4" /> Additional info requested: {app.info_request}</div>
      )}

      <SectionCard title="Business Information" icon={FileText} testid="seller-business-form">
        <div className="grid gap-3 sm:grid-cols-2">
          {[["seller_name", "Business / Seller name *"], ["business_type", "Business type"], ["contact_person", "Contact person"], ["phone", "Mobile number"], ["state", "State"], ["district", "District"], ["pincode", "Pincode"], ["pan", "PAN"], ["gst", "GST (if any)"]].map(([k, ph]) => (
            <Input key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} data-testid={`seller-field-${k}`} />
          ))}
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Business address" className="sm:col-span-2" data-testid="seller-field-address" />
          <Input value={form.intended_products} onChange={(e) => setForm({ ...form, intended_products: e.target.value })} placeholder="What do you want to sell? e.g. packaged food and organic turmeric" className="sm:col-span-2" data-testid="seller-field-intended" />
          <div className="sm:col-span-2" data-testid="seller-categories-picker">
            <label className="text-xs font-medium text-slate-500">Categories you want to sell in — each is reviewed &amp; approved independently</label>
            <select value="" onChange={(e) => { addCat(e.target.value); e.target.value = ""; }} data-testid="seller-cat-add" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600">
              <option value="">Add a category…</option>
              {cats.map((c) => <option key={c.category_id} value={c.category_id} disabled={catIds.includes(c.category_id)}>{c.parent_id ? "— " : ""}{c.name}</option>)}
            </select>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="seller-cat-chips">
              {catIds.length === 0 && <span className="text-xs text-slate-400">No categories selected yet.</span>}
              {catIds.map((id) => { const st = catStatus(id); return (
                <span key={id} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700" data-testid={`seller-cat-chip-${id}`}>
                  {catName(id)}
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold capitalize ${st.status === "approved" ? "bg-emerald-100 text-emerald-700" : st.status === "rejected" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{st.status}</span>
                  <button type="button" onClick={() => removeCat(id)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
                </span>); })}
            </div>
            <button type="button" onClick={saveCats} data-testid="seller-cat-save" className="mt-2 rounded-full border border-blue-300 bg-white px-4 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50">Save categories for review</button>
          </div>
        </div>
        <button onClick={saveInfo} disabled={saving} data-testid="seller-save-info" className="mt-4 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{app ? "Update & detect requirements" : "Save & detect requirements"}</button>
        {app?.ai_note && <p className="mt-2 text-xs text-slate-400">{app.ai_note}</p>}
        <div className="mt-3 rounded-xl border border-violet-100 bg-violet-50/30 p-3" data-testid="seller-ai-autofill">
          <p className="text-xs font-semibold text-slate-700">Have your GST or PAN handy? Let AI fill it in.</p>
          <AIDocAssist docType="auto" onExtract={applyDocFields} label="Auto-fill from a document" hint="Upload your GST certificate, PAN or trade licence — AI reads the details and fills the form above. Optional; you can also type everything yourself." />
        </div>
      </SectionCard>

      {reqs.length > 0 && (
        <SectionCard title="Documents" icon={ShieldCheck} testid="seller-documents">
          <p className="mb-3 text-xs text-slate-400">Suggested based on what you plan to sell. Uploading is optional — our team will ask if anything specific is needed. Upload valid PDF/JPG/PNG files (max 8 MB). Documents remain private.</p>
          <div className="space-y-3">
            {reqs.map((r) => {
              const d = docFor(r.code);
              const st = d?.status || (r.required ? "required" : "not_uploaded");
              return (
                <div key={r.code} className="rounded-xl border border-slate-100 p-3" data-testid={`seller-doc-${r.code}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{r.name} <span className="text-xs text-slate-400">(optional)</span></p>
                      <p className="text-xs text-slate-400">{r.why}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${badge(st)}`}>{DOC_LABEL[st] || st}</span>
                  </div>
                  {d?.admin_comment && <p className="mt-1 text-xs text-amber-600">Admin: {d.admin_comment}</p>}
                  {d?.ai_findings?.length > 0 && <p className="mt-1 text-xs text-slate-400">AI findings: {d.ai_findings.join("; ")}</p>}
                  {app?.status !== "approved" && (
                    <div className="mt-2 flex items-center gap-2">
                      <input ref={(el) => (fileRefs.current[r.code] = el)} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" data-testid={`seller-file-${r.code}`}
                        onChange={(e) => upload(r.code, r.name, e.target.files?.[0], r.expiry ? prompt("Enter expiry date (YYYY-MM-DD), or leave blank:") : null)} />
                      <button onClick={() => fileRefs.current[r.code]?.click()} data-testid={`seller-upload-${r.code}`} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-blue-300">
                        <Upload className="h-3 w-3" /> {d ? "Replace" : "Upload"}
                      </button>
                      {d?.expiry_date && <span className="text-xs text-slate-400">Expires {d.expiry_date}</span>}
                    </div>
                  )}
                  {app?.status !== "approved" && (
                    <AIDocAssist docType={docTypeForCode(r.code)} requiredDoc={!!r.required} onExtract={applyDocFields} />
                  )}
                </div>
              );
            })}
          </div>
          {app?.status !== "approved" && app?.status !== "admin_review" && (
            <button onClick={submit} data-testid="seller-submit-app" className="mt-4 inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white"><Clock className="h-4 w-4" /> Submit for verification</button>
          )}
        </SectionCard>
      )}
    </div>
  );
}
